import React from "react";
import { Check, X, ArrowRight, Zap, DollarSign, Gauge, RefreshCw } from "lucide-react";
import Navbar from "../../components/Navbar";
const comparisonData = {
  weaviate: {
    name: "Weaviate",
    tagline: "GraphQL-powered vector database with AI modules",
    pros: [
      "Native GraphQL API for flexible queries",
      "Built-in AI modules (vectorisers, Q&A, summarisation)",
      "Strong semantic search and hybrid capabilities",
      "Open-source with self-hosting flexibility",
      "Automatic schema inference and data classification"
    ],
    cons: [
      "Steeper learning curve with GraphQL",
      "Higher resource consumption at scale",
      "Complex production configuration",
      "Cloud pricing can escalate quickly",
      "Slower query performance with large payloads"
    ],
    pricing: {
      tier: "Moderate",
      description: "Free self-hosted. Serverless cloud starts at $25/month with usage-based scaling.",
      note: "Best for: Teams leveraging AI modules and semantic search features"
    },
    performance: {
    
      latency: "Sub-150ms average query time",
      throughput: "Good for hybrid and semantic search"
    }
  },
  pinecone: {
    name: "Pinecone",
    tagline: "Fully managed vector database for production",
    pros: [
      "Zero infrastructure management required",
      "Excellent performance and low latency",
      "Simple REST API with great developer experience",
      "Proven scalability to billions of vectors",
      "Enterprise-grade support and SLAs"
    ],
    cons: [
      "Premium pricing at scale",
      "No built-in AI modules (bring your own)",
      "Vendor lock-in concerns",
      "Less flexibility in query structure",
      "Limited control over infrastructure"
    ],
    pricing: {
      tier: "Premium",
      description: "Starts at $70/month for 100K vectors. Scales based on pod size and replicas.",
      note: "Best for: Production apps prioritising simplicity and reliability"
    },
    performance: {

      latency: "Sub-50ms p99 latency",
      throughput: "Optimised for pure vector similarity"
    }
  }
};

const migrationDetails = {
  difficulty: "Medium",
  timeEstimate: "3-6 hours for 1M vectors",
  considerations: [
    "GraphQL to REST API query translation",
    "Schema mapping from classes to namespaces",
    "Built-in vectorisers need external replacement",
    "Cross-reference handling requires restructuring"
  ],
  howWeHelp: [
    "Automated GraphQL to REST API conversion",
    "Schema transformation and namespace mapping",
    "Vectoriser migration strategy guidance",
    "Zero-downtime migration with validation",
    "Performance optimisation recommendations"
  ]
};

const ComparisonSection = ({ title, icon: Icon, children }) => (
  <div className="bg-gray-900 rounded-xl p-8 border border-gray-800 mb-8">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
        <Icon className="w-5 h-5 text-black" />
      </div>
      <h2 className="text-2xl font-bold text-white">{title}</h2>
    </div>
    {children}
  </div>
);

const ProConList = ({ items, isPositive }) => (
  <ul className="space-y-3">
    {items.map((item, idx) => (
      <li key={idx} className="flex items-start gap-3">
        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
          isPositive ? 'bg-white/20' : 'bg-gray-700'
        }`}>
          {isPositive ? (
            <Check className="w-3 h-3 text-green-500" />
          ) : (
            <X className="w-3 h-3 text-red-500" />
          )}
        </div>
        <span className="text-gray-300 text-sm">{item}</span>
      </li>
    ))}
  </ul>
);

const DatabaseCard = ({ data }) => (
  <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
    <h3 className="text-2xl font-bold text-white mb-2">{data.name}</h3>
    <p className="text-gray-400 text-sm mb-6">{data.tagline}</p>
    
    <div className="space-y-6">
      <div>
        <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
          <Check className="w-4 h-4" />
          Advantages
        </h4>
        <ProConList items={data.pros} isPositive={true} />
      </div>
      
      <div>
        <h4 className="text-gray-400 font-semibold mb-3 flex items-center gap-2">
          <X className="w-4 h-4" />
          Limitations
        </h4>
        <ProConList items={data.cons} isPositive={false} />
      </div>
    </div>
  </div>
);

const PricingCard = ({ data, name }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-xl font-bold text-white">{name}</h3>
      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-800 text-gray-300 border border-gray-700">
        {data.tier}
      </span>
    </div>
    <p className="text-gray-300 text-sm mb-3">{data.description}</p>
    <p className="text-gray-500 text-xs italic">{data.note}</p>
  </div>
);

const PerformanceCard = ({ data, name }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-xl font-bold text-white">{name}</h3>
    </div>
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-gray-400">Query Latency:</span>
        <span className="text-gray-300">{data.latency}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="text-gray-400">Throughput:</span>
        <span className="text-gray-300">{data.throughput}</span>
      </div>
    </div>
  </div>
);

export default function WeaviateVsPinecone() {
  return (
    <div className="bg-black min-h-screen px-6">
      <Navbar />
      <div className="max-w-6xl py-28 mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-4">
            Weaviate vs Pinecone: Comparison 202
          </h1>
          <p className="text-gray-400 text-xl max-w-3xl mx-auto">
            GraphQL flexibility vs API simplicity. Compare features, pricing, performance, 
            and migration complexity to choose the right vector database for your needs.
          </p>
        </div>

        {/* Side-by-Side Comparison */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          <DatabaseCard data={comparisonData.weaviate} />
          <DatabaseCard data={comparisonData.pinecone} />
        </div>

        {/* Pricing Comparison */}
        <ComparisonSection title="Pricing Comparison" icon={DollarSign}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <PricingCard data={comparisonData.weaviate.pricing} name="Weaviate" />
            <PricingCard data={comparisonData.pinecone.pricing} name="Pinecone" />
          </div>
          <div className="mt-8 p-6 bg-gray-800 border border-gray-700 rounded-lg">
            <p className="text-gray-300 text-sm">
              <strong className="text-white">Cost Analysis:</strong> Weaviate offers more budget-friendly 
              options with free self-hosting and lower cloud entry points ($25 vs $70/month). However, 
              both can scale to similar costs at enterprise levels. For 5M vectors, Weaviate typically 
              costs 20-30% less, but Pinecone's predictable pricing may be easier to budget.
            </p>
          </div>
        </ComparisonSection>

        {/* Performance Comparison */}
        <ComparisonSection title="Performance Metrics" icon={Gauge}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <PerformanceCard data={comparisonData.weaviate.performance} name="Weaviate" />
            <PerformanceCard data={comparisonData.pinecone.performance} name="Pinecone" />
          </div>
          <div className="mt-8 p-6 bg-gray-800 border border-gray-700 rounded-lg">
            <p className="text-gray-300 text-sm">
  <strong className="text-white">Performance Considerations:</strong> Performance varies based on
  scale, workload characteristics, and configuration. Weaviate is well-suited for semantic and
  hybrid search use cases due to its native support for vector + metadata querying and AI modules.
  For large-scale, latency-sensitive workloads, performance depends heavily on indexing strategy,
  infrastructure, and tuning rather than a single platform being universally superior.
</p>

          </div>
        </ComparisonSection>

        {/* Migration Difficulty */}
        <ComparisonSection title="Migration from Weaviate to Pinecone" icon={RefreshCw}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Migration Complexity</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Difficulty Level:</span>
                  <span className="text-gray-300 font-semibold">{migrationDetails.difficulty}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Estimated Time:</span>
                  <span className="text-gray-300 text-sm">{migrationDetails.timeEstimate}</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Key Considerations</h3>
              <ul className="space-y-2">
                {migrationDetails.considerations.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </ComparisonSection>

        {/* How Vector Migration Helps */}
        <div className="mt-16 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-12 text-center border border-gray-700">
          <div className="max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
              <Zap className="w-8 h-8 text-black" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">
              How Vector Migration Simplifies Your Switch
            </h2>
            <p className="text-gray-400 mb-8">
              We handle the complexity of migrating from Weaviate to Pinecone, including GraphQL 
              conversion, schema mapping, and vectoriser replacement strategies.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 text-left">
              {migrationDetails.howWeHelp.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-black/40 rounded-lg p-4 border border-gray-800">
                  <Check className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300 text-sm">{item}</span>
                </div>
              ))}
            </div>
            
            {/* <button className="bg-white text-black px-8 py-4 rounded-lg font-semibold hover:bg-gray-200 transition-colors inline-flex items-center gap-2">
              Start Your Migration Today
              <ArrowRight className="w-5 h-5" />
            </button> */}
          </div>
        </div>

        {/* Final Recommendation */}
        <div className="mt-16 bg-gray-900 border border-gray-800 rounded-xl p-8">
          <h2 className="text-2xl font-bold text-white mb-4">Which Should You Choose?</h2>
          <div className="space-y-4 text-gray-300">
            <p>
              <strong className="text-white">Choose Weaviate if:</strong> You need built-in AI modules 
              (vectorisers, Q&A, summarisation), prefer GraphQL's flexible query structure, want 
              self-hosting control, or require extensive semantic search capabilities without external 
              dependencies.
            </p>
            <p>
              <strong className="text-white">Choose Pinecone if:</strong> You prioritise simplicity 
              and speed, need guaranteed low latency at any scale, prefer fully managed infrastructure 
              with minimal DevOps, or want predictable performance for customer-facing applications.
            </p>
            <p className="text-sm text-gray-500 italic">
              Both are enterprise-ready solutions. Weaviate offers more features out-of-the-box with 
              AI modules, while Pinecone focuses on doing one thing exceptionally well: fast, reliable 
              vector search. Choose based on whether you value feature richness or operational simplicity.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}